import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {ProjectsSidepanelContext} from 'nav/sidepanel/listing/ProjectsSidepanelContext';
import ManageTemplateCollectionModal from 'notebook/entry-templates/collections/ManageTemplateCollectionModal';
import getDefaultLibrarySearchParams from 'notebook/library/library-sidepanel/getDefaultLibrarySearchParams';
import * as FilterUtil from 'search/FilterUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, SearchParams} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  TemplateCollectionQuery,
  TemplateCollectionQueryVariables,
} from './graphql/__generated/TemplateCollectionQuery';
import {TEMPLATE_COLLECTION_QUERY} from './graphql/TemplateCollectionsQueries';

interface TemplateCollectionsSidepanelCrumbsProps {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}

/**
 * A provider of Crumbs for the TemplateCollectionsSidepanel.
 *
 * Template Collections are just a single level of organization with all of the Collectibles living inside of
 * one Collection. So our Sidepanel will only ever be in one of two states:
 * - viewing the list of Template Collections (e.g. our root)
 * - viewing the list of Collectibles inside of a Template Collection
 *
 * We can tell when we are at the root level by the absence of a Template Collection Id within the
 * TemplateCollectionFilter.  When that is the case, we just show a single Crumb for "Template Collections".
 *
 * But, if we have a `templateCollectionId`, then we want to show an additional Crumb for the Template
 * Collection that we're currently viewing. In this case, we'll use the `TemplateCollectionQuery` to fetch the
 * TemplateCollection fragment.
 *
 * @param activeSavedSearch - the currently active SavedSearch
 * @param onSelectSavedSearch - a function to call when a SavedSearch is selected
 * @param onSearch - a function to call when we want to search some new SearchParams
 * @param searchParams - the current SearchParams
 * @param settingsComponents - any additional components to render in the Settings dropdown
 */
export default function TemplateCollectionsSidepanelCrumbs(
  props: TemplateCollectionsSidepanelCrumbsProps
): JSX.Element {
  const templateCollectionId = FilterUtil.getFilterValueByKey(
    props.searchParams.filters,
    FilterKey.TEMPLATE_COLLECTION
  ) as string | null;

  if (templateCollectionId == null) {
    return <InnerTemplateCollectionsSidepanelCrumbs {...props} templateCollection={undefined} />;
  }

  return (
    <BenchlingQuery<TemplateCollectionQuery, TemplateCollectionQueryVariables>
      query={TEMPLATE_COLLECTION_QUERY}
      variables={{id: templateCollectionId}}
      renderLoading={() => <HorizontalThrobber />}
    >
      {(data) => {
        if (isInaccessible(data.templateCollection)) {
          // It could be that the user was signed out
          return null;
        }
        return (
          <InnerTemplateCollectionsSidepanelCrumbs {...props} templateCollection={data.templateCollection} />
        );
      }}
    </BenchlingQuery>
  );
}

function InnerTemplateCollectionsSidepanelCrumbs(
  props: TemplateCollectionsSidepanelCrumbsProps & {
    templateCollection?: ExcludeInaccessible<TemplateCollectionQuery['templateCollection']>;
  }
): JSX.Element {
  const crumbs: Array<JSX.Element> = [];
  let settingsIcon = props.settingsComponents;
  crumbs.push(
    <ProjectsSidepanelContext.Consumer key="template-collections">
      {({rootCrumbName}) => (
        <CrumbLink
          key="sidepanel-root"
          text={rootCrumbName}
          onClick={() => {
            props.onSearch(
              SearchUtil.clearSearchParams(props.searchParams, {
                preservedFilterKeys: [],
                filtersToReset: getDefaultLibrarySearchParams().filters,
              })
            );
          }}
        />
      )}
    </ProjectsSidepanelContext.Consumer>
  );
  if (props.templateCollection != null) {
    const key = `template-collection-${props.templateCollection.id}`;
    crumbs.push(<CrumbLink key={key} text={props.templateCollection.name} />);
    settingsIcon = (
      <>
        <TemplateCollectionSettingsIcon templateCollection={props.templateCollection} />
        {props.settingsComponents}
      </>
    );
  }

  if (props.activeSavedSearch) {
    crumbs.push(<CrumbLink key="saved-search-root" text={props.activeSavedSearch.name} />);
  }

  return <SplitBreadcrumbs crumbs={crumbs} settingsIcon={settingsIcon} />;
}

interface TemplateCollectionSettingsIconProps {
  templateCollection: ExcludeInaccessible<TemplateCollectionQuery['templateCollection']>;
}

function TemplateCollectionSettingsIcon(props: TemplateCollectionSettingsIconProps): JSX.Element {
  const onClick = useCallback(() => {
    legacyRunModal(ManageTemplateCollectionModal, {
      // We don't want to go to the Template Collection's URL if we're using the search sidepanel.
      navigateOnSuccess: !BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE,
      templateCollection: props.templateCollection,
    });
  }, [props.templateCollection]);

  return <SettingsIcon className="u-tiny-spacing-left" onClick={onClick} />;
}
